import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Leaf,
  TrendingDown,
  Zap,
  Droplets,
  Recycle,
  BarChart3,
  Calendar,
  Target,
  Award,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

export default function TumSustainability() {
  const [activeTab, setActiveTab] = useState('overview');

  // Dummy data for sustainability metrics
  const carbonFootprint = [
    { month: 'Jan', carbon: 1250, target: 1000 },
    { month: 'Feb', carbon: 1180, target: 1000 },
    { month: 'Mar', carbon: 1320, target: 1000 },
    { month: 'Apr', carbon: 1100, target: 1000 },
    { month: 'May', carbon: 1050, target: 1000 },
    { month: 'Jun', carbon: 980, target: 1000 },
  ];

  const energyEfficiency = [
    { location: 'Property A', efficiency: 85, target: 90 },
    { location: 'Property B', efficiency: 78, target: 85 },
    { location: 'Property C', efficiency: 92, target: 90 },
    { location: 'Property D', efficiency: 75, target: 80 },
  ];

  const sustainabilityScore = 82;
  const energyReduction = 15.5;
  const waterConservation = 12.3;
  const wasteReduction = 8.7;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold tracking-tight">Sustainability</h1>
        <p className="text-muted-foreground mt-2 text-lg">
          Track and improve your environmental impact through utility management
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Sustainability Score</p>
                <p className="text-2xl font-bold text-green-600">{sustainabilityScore}%</p>
                <p className="text-xs text-muted-foreground mt-1">Above average</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Leaf className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Energy Reduction</p>
                <p className="text-2xl font-bold text-blue-600">{energyReduction}%</p>
                <p className="text-xs text-muted-foreground mt-1">vs. last year</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Water Conservation</p>
                <p className="text-2xl font-bold text-cyan-600">{waterConservation}%</p>
                <p className="text-xs text-muted-foreground mt-1">vs. baseline</p>
              </div>
              <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <Droplets className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Waste Reduction</p>
                <p className="text-2xl font-bold text-emerald-600">{wasteReduction}%</p>
                <p className="text-xs text-muted-foreground mt-1">recycling rate</p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <Recycle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b border-border">
          <TabsList className="w-full h-auto p-0 bg-transparent justify-start overflow-x-auto flex-nowrap gap-1">
            <TabsTrigger
              value="overview"
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="carbon"
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <TrendingDown className="h-4 w-4" />
              Carbon Footprint
            </TabsTrigger>
            <TabsTrigger
              value="efficiency"
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Target className="h-4 w-4" />
              Energy Efficiency
            </TabsTrigger>
            <TabsTrigger
              value="certifications"
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Award className="h-4 w-4" />
              Certifications
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Carbon Footprint Trend</CardTitle>
                <CardDescription>Monthly carbon emissions vs. targets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={carbonFootprint}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="carbon" stroke="#ef4444" name="Actual" />
                      <Line type="monotone" dataKey="target" stroke="#10b981" name="Target" strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Energy Efficiency by Property</CardTitle>
                <CardDescription>Efficiency scores compared to targets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {energyEfficiency.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{item.location}</span>
                        <Badge variant={item.efficiency >= item.target ? 'default' : 'secondary'}>
                          {item.efficiency}%
                        </Badge>
                      </div>
                      <Progress value={(item.efficiency / item.target) * 100} className="h-2" />
                      <p className="text-xs text-muted-foreground">Target: {item.target}%</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="carbon" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Carbon Emissions Analysis</CardTitle>
              <CardDescription>Detailed breakdown of carbon footprint by utility type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={carbonFootprint}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="carbon" fill="#ef4444" name="Carbon (tons CO2)" />
                    <Bar dataKey="target" fill="#10b981" name="Target" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="efficiency" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Energy Efficiency Metrics</CardTitle>
              <CardDescription>Property-level efficiency tracking and recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={energyEfficiency}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="location" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="efficiency" fill="#3b82f6" name="Efficiency %" />
                    <Bar dataKey="target" fill="#10b981" name="Target %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certifications" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-l-4 border-l-green-500">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-green-600" />
                  <CardTitle>Energy Star</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Certified Properties</span>
                    <Badge variant="default">12</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Pending Certification</span>
                    <Badge variant="secondary">3</Badge>
                  </div>
                  <Progress value={80} className="h-2" />
                  <p className="text-xs text-muted-foreground">80% of properties certified</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Leaf className="h-5 w-5 text-blue-600" />
                  <CardTitle>LEED Certification</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">LEED Gold</span>
                    <Badge variant="default">5</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">LEED Silver</span>
                    <Badge variant="secondary">8</Badge>
                  </div>
                  <Progress value={65} className="h-2" />
                  <p className="text-xs text-muted-foreground">65% of properties certified</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

